export type LeaderPageLanguage = 'en' | 'ar';

export type LeaderPageContent = {
  nav: {
    about: string;
    methodology: string;
    sectors: string;
    list: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    cta: string;
    stats: Array<{ label: string; value: string }>;
    marquee: string[];
  };
  about: {
    heading: string;
    lead: string;
    description: string[];
    metrics: Array<{ value: string; label: string }>;
  };
  methodology: {
    heading: string;
    lead: string;
    pillars: Array<{ title: string; description: string }>;
    eligibilityText: string;
  };
  sectors: {
    heading: string;
    lead: string;
    items: string[];
  };
  list: {
    heading: string;
    lead: string;
    subLead: string;
    all: string;
    hiddenTitle: string;
    hiddenBody: string;
    popupTitle: string;
  };
  languageToggle: {
    english: string;
    arabic: string;
  };
};

export const leaderPageText: Record<LeaderPageLanguage, LeaderPageContent> = {
  en: {
    nav: {
      about: 'About',
      methodology: 'Methodology',
      sectors: 'Sectors',
      list: 'The List',
    },
    hero: {
      eyebrow: 'CNBC Arabia · Top 100 List · 2026',
      title: "The 100 Business Leaders <accent>Defining</accent> the Region's Economy",
      subtitle:
        'This list is curated, non-ranked compilation intended to highlight Influence and Impact rather than provide ordered ranking.',
      cta: 'Explore the list',
      stats: [
        { value: '100', label: 'Leaders' },
        { value: '11', label: 'Sectors' },
        { value: '7', label: 'Markets' },
        { value: '1', label: 'Definitive list' },
      ],
      marquee: [
        'Banking & Investment',
        'Energy & Utilities',
        'Technology & AI',
        'Real Estate',
        'Healthcare',
        'Retail & Consumer',
        'Logistics & Trade',
      ],
    },
    about: {
      heading: 'About The Top 100 Leaders',
      lead:
        'Not a Popularity Contest. A Measure of <accent>Impact</accent> — The Leaders Whose Decisions Actually Move the Region’s Markets.',
      description: [
        "Top 100 Leaders is CNBC Arabia's flagship recognition of the people shaping the Middle East's economic story.",
        "It spans boardrooms of the region's largest institutions, founders building next-generation companies, and policymakers drawing the map.",
        'Nominations are reviewed by editors and independent advisors against a published framework focused on measurable performance.',
      ],
      metrics: [
        { value: '100', label: 'Leaders recognised' },
        { value: '11', label: 'Sectors covered' },
        { value: '7', label: 'Markets' },
        { value: '$3.96 Trillion', label: 'Combined market value*' },
      ],
    },
    methodology: {
      heading: 'Methodology',
      lead:
        'Five Tests. <accent>One</accent> Standard. Every leader is Assessed Against the Same Framework',
      pillars: [
        {
          title: 'Financial performance',
          description:
            'Assesses Leader’s ability to generate sustainable financial growth and shareholder value.',
        },
        {
          title: 'Market impact',
          description:
            'Measurable effect on sector outcomes, shareholder value and the wider economy.',
        },
        {
          title: 'Innovation',
          description:
            'Building products, models or institutions that did not exist before.',
        },
        {
          title: 'Governance',
          description:
            'Leadership that endures scrutiny and sets standards for peers.',
        },
        {
          title: 'Resilience',
          description:
            'Performance sustained through the full economic cycle, not a single year.',
        },
      ],
      eligibilityText:
        'Eligible leaders should have led a company headquartered in or with significant operations across the MENA region and have served in their role for at least one year. Companies included in CNBC Arabia Top100 list must have verifiable operational and financial records, regardless of whether they are publicly listed or privately held. Privately held companies qualify provided sufficient public disclosure is available.',
    },
    sectors: {
      heading: 'Coverage',
      lead: 'Eleven Sectors, <accent>One</accent> Economy',
      items: [
        'Banking & Investment',
        'Energy & Utilities',
        'Technology & AI',
        'Real Estate',
        'Healthcare',
        'Retail & Consumer',
        'Logistics & Trade',
        'Telecom & Media',
        'Industrials',
        'Aviation & Transport',
        'Fintech',
      ],
    },
    list: {
      heading: 'The List · Preview',
      lead: 'A First Look at the <accent>2026</accent> List',
      subLead:
        '',
      all: 'All',
      hiddenTitle: 'Top 100 list is currently hidden',
      hiddenBody:
        'The full ranking is locked for launch window and will be accessible after the official release date.',
      popupTitle: 'Leader profile',
    },
    languageToggle: {
      english: 'English',
      arabic: 'Arabic',
    },
  },
  ar: {
    nav: {
      about: 'نبذة',
      methodology: 'المنهجية',
      sectors: 'القطاعات',
      list: 'القائمة',
    },
    hero: {
      eyebrow: 'لائحة CNBC  عربية لقادة الأعمال - 2026',
      title: '100 من أبرز قادة الأعمال الذين <accent>يشكّلون</accent> ملامح اقتصاد المنطقة',
      subtitle:
        'تم إعداد هذه القائمة بعناية لكن من دون أي تصنيف أو ترتيب محدد، وتهدف إلى تسليط الضوء على قادة الأعمال الأكثر نفوذاً والأوسع تأثيراً في المنطقة.',
      cta: 'استكشف القائمة',
      stats: [
        { value: '100', label: 'قيادي' },
        { value: '11', label: 'قطاعاً' },
        { value: '7', label: 'أسواق' },
        { value: '1', label: 'لائحة واحدة!' },
      ],
      marquee: [
        'البنوك والاستثمار',
        'الطاقة والمرافق',
        'التكنولوجيا والذكاء الاصطناعي',
        'العقارات',
        'الرعاية الصحية',
        'التجزئة والسلع الاستهلاكية',
        'الخدمات اللوجستية والتجارة',
      ],
    },
    about: {
      heading: 'لائحة CNBC  عربية لقادة الأعمال - 2026',
      lead:
        'لا تُعد <accent>لائحة المئة قيادي</accent> مسابقة لقياس مدى الشعبية أو الحضور الإعلامي، بل مسعىً لإظهار حجم التأثير الحقيقي الذي يمارسه قادة الأعمال ومؤسساتهم في الاقتصاد والأسواق في المنطقة',
      description: [
        'لائحة Top100 من قياديي الأعمال تُجسد إحدى المبادرات الرائدة لـ CNBC عربية للإضاءة على شخصيات تسهم في رسم المشهد الاقتصادي.',
        'وتضم نخبة من قادة المؤسسات الكبرى والمؤسِسين والتنفيذيين الذين يقودون الجيل الجديد من الشركات ويرسمون ملامح مستقبل الأعمال في المنطقة.',
        'وتخضع جميع الترشيحات لإطار تقييم واضح ومعلن يرتكز على الأداء والإنجازات القابلة للقياس.',
      ],
      metrics: [
        { value: '100', label: 'قيادي' },
        { value: '11', label: 'قطاعاً' },
        { value: '7', label: 'أسواق' },
        { value: '3.96  تريليون $', label: 'القيمة السوقية المجمعة*' },
      ],
    },
    methodology: {
      heading: 'منهجية التقييم',
      lead:
        'خمسة معايير رئيسية. إطار محدد. منهجية واحدة لاختيار قادة الأعمال',
      pillars: [
        {
          title: 'الأداء المالي',
          description:
            'مدى قدرة القيادي على تحقيق الأرباح العائدة على المساهمين والنمو ',
        },
                {
          title: 'التأثير الاقتصادي',
          description:
            'حجم الأثر الملموس الذي يحققه القيادي في قطاعه  وعلى مساهمي شركته  وفي الاقتصاد الكلي',
        },
        {
          title: 'الابتكار',
          description:
            'القدرة على تطوير منتجات أو نماذج أعمال أو مؤسسات جديدة قادرة على المنافسة والنمو',
        },
        {
          title: 'الحوكمة',
          description:
            'قيادة تتسم بالشفافية والمسؤولية وقادرة على الصمود أمام التدقيق والامتثال',
        },
        {
          title: 'المرونة والاستدامة',
          description:
            'تحقيق أداء قوي ومستدام عبر مختلف الدورات الاقتصادية',
        },
      ],
      eligibilityText:
        'يُشترط في قادة الأعمال المؤهلين للانضمام للائحة "المئة" أن يكونوا قد تولوا قيادة شركة يقع مقرها الرئيسي في المنطقة أو تمارس عمليات رئيسية فيها، وأن يكونوا قد أمضوا ما لا يقل عن عام واحد في مناصبهم القيادية.\nكما يشترط أن تتوفر للشركات ضمن قائمة أفضل 100 قيادي سجلات تشغيلية ومالية موثقة، سواء كانت لشركات مدرجة في الأسواق المالية أو مملوكة للقطاع الخاص.',
    },
    sectors: {
      heading: 'القطاعات',
      lead: 'أحد عشر قطاعاً  اقتصادٌ واحد',
      items: [
        'البنوك والاستثمار',
        'الطاقة والمرافق',
        'التكنولوجيا والذكاء الاصطناعي',
        'العقارات',
        'الرعاية الصحية',
        'التجزئة والسلع الاستهلاكية',
        'الخدمات اللوجستية والتجارة',
        'الاتصالات والإعلام',
        'الصناعات',
        'الطيران والنقل',
        'التكنولوجيا المالية',
      ],
    },
    list: {
      heading: 'القائمة · لمحة أولية',
      lead: 'نظرة أولى على قائمة <accent>2026</accent>',
      subLead: '',
      all: 'الكل',
      hiddenTitle: 'قائمة أفضل 100 قائد مخفية حاليًا',
      hiddenBody:
        'القائمة الكاملة غير متاحة خلال فترة الإطلاق، وستصبح متاحة بعد تاريخ الإصدار الرسمي.',
      popupTitle: 'ملف القائد',
    },
    languageToggle: {
      english: 'English',
      arabic: 'العربية',
    },
  },
};

export const LEADER_RELEASE_CONFIG = {
  // Keep true for development preview. Switch to false for production release behavior.
  forceListVisible: true,
  // Optional kill switch for launch-hide override in development.
  forceHiddenWindow: false,
  launchDate: {
    monthIndex: 6, // July (0-indexed)
    day: 6,
  },
  releaseDate: {
    monthIndex: 6, // July (0-indexed)
    day: 15,
  },
};

const createReleaseDate = (
  now: Date,
  monthIndex: number,
  day: number
): Date => {
  return new Date(now.getFullYear(), monthIndex, day, 0, 0, 0, 0);
};

export const getLeadersLaunchDate = (now: Date = new Date()): Date => {
  return createReleaseDate(
    now,
    LEADER_RELEASE_CONFIG.launchDate.monthIndex,
    LEADER_RELEASE_CONFIG.launchDate.day
  );
};

export const getLeadersReleaseDate = (now: Date = new Date()): Date => {
  return createReleaseDate(
    now,
    LEADER_RELEASE_CONFIG.releaseDate.monthIndex,
    LEADER_RELEASE_CONFIG.releaseDate.day
  );
};

export const isLeadersListHiddenBySchedule = (
  now: Date = new Date()
): boolean => {
  if (LEADER_RELEASE_CONFIG.forceHiddenWindow) {
    return true;
  }

  const launchDate = getLeadersLaunchDate(now);
  const releaseDate = getLeadersReleaseDate(now);

  return now >= launchDate && now < releaseDate;
};

export const shouldShowLeadersList = (now: Date = new Date()): boolean => {
  if (LEADER_RELEASE_CONFIG.forceListVisible) {
    return true;
  }

  const releaseDate = getLeadersReleaseDate(now);
  return now >= releaseDate;
};
